import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { Attribute, AttributeCode, useModelService } from 'services/model/model.service';
import { Filters } from 'services/data.service';
import { MeasureCardRow } from 'components/MeasureCardRow';
import {
  useProfitabilityFeesAndCosts,
  useProfitabilityLensBreakdownRows,
  useProfitabilityOverviewKpis,
} from 'components/profitability/queryHooks';
import { useDataRequest } from 'hooks/useDataRequest';
import { ProfitabilityWaterfallChart } from 'components/profitability/ProfitabilityWaterfallChart';
import { CostOfGoodsList } from 'components/profitability/CostOfGoodsList';
import { FeesList } from 'components/profitability/FeesList';
import { LogisticsCostsList } from 'components/profitability/LogisticsCostsList';
import { StyledToggleButton } from 'components/StyledToggleButton';
import { ToggleButtonGroup } from '@mui/material';
import { DataTable } from 'components/table/DataTable';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { useProfitabilityLensColumns } from 'components/profitability/buildProfitabilityColumns';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { articleLink, productLink, styleLink } from 'pages/entities/entityhelper';
import { toFeesAndCostsValueMeasure } from 'components/profitability/profitabilityMeasures';
import { LensRow } from 'components/profitability/types';
import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { ProfitabilityEntityInfoContent } from 'pages/entities/components/ProfitabilityEntityInfoContent';

export interface PerformanceTabProps {
  filterDecorator: (filter: Filters) => Filters;
  availableTables: Attribute[];
  showCostOfGoods?: boolean;
}

export function ProfitabilityTab({ filterDecorator, availableTables, showCostOfGoods = true }: PerformanceTabProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { createAttributeColumn, getAttributeArrayForLeadAttribute } = useDataEntriesInTables();
  const { setContent } = useInfoDrawer();

  useEffect(() => {
    setContent({
      titleKey: 'entities.lens.profitability',
      content: <ProfitabilityEntityInfoContent />,
    });
    return () => setContent(null);
  }, [setContent]);

  const requestData = useDataRequest();
  const adjustedRequestData = { ...requestData, filters: filterDecorator(requestData.filters) };
  const isComparisonActive = Boolean(
    requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
  );

  const { getMeasures } = useModelService();
  const [netSoldGmv, cm1Val, slsFeeValue, cm2Val] = getMeasures([
    'netSlsGmvValExtExpOD',
    'cm1ValExtExpOD',
    'netSlsFeeValExtExpOD',
    'cm2ValExtExpOD',
  ]);
  const measures = [netSoldGmv, cm1Val, toFeesAndCostsValueMeasure(slsFeeValue), cm2Val];

  const { data: totalsData } = useProfitabilityOverviewKpis(adjustedRequestData);
  const { data: feesAndCostsData } = useProfitabilityFeesAndCosts(adjustedRequestData);

  const availableAttributeCodes = availableTables.map(att => att.code);
  const [tableAttribute, setTableAttribute] = useState<AttributeCode>(availableAttributeCodes[0]);

  const breakdownQueryKey = `profitability-lens-${tableAttribute}-${JSON.stringify(adjustedRequestData.filters)}`;

  const { data: tableData } = useProfitabilityLensBreakdownRows(tableAttribute, adjustedRequestData, breakdownQueryKey);

  const tableTitleId = `profitability.drillDown.dimension.${tableAttribute}`;

  const tableAttributes = getAttributeArrayForLeadAttribute(tableAttribute);
  const attributeColumns = tableAttributes.map(attribute => createAttributeColumn(attribute));

  const profitabilityColumns = useProfitabilityLensColumns();

  const columns = [...attributeColumns, ...profitabilityColumns] as ColumnDef<LensRow>[];

  const getRowClick = () => {
    switch (tableAttribute) {
      case 'styBrandColor':
        return (row: Row<LensRow>) => {
          void navigate(styleLink(row.original.prdNumber ?? '', row.original.styBrandColor ?? '', 'profitability'));
        };
      case 'artEan':
        return (row: Row<LensRow>) => {
          void navigate(articleLink(row.original.artEan ?? '', 'profitability'));
        };
      case 'prdNumber':
        return (row: Row<LensRow>) => {
          void navigate(productLink(row.original.prdNumber ?? '', 'profitability'));
        };
      default:
        return undefined;
    }
  };

  if (!totalsData) return;

  return (
    <div className="xl:col-span-12 col-span-6 flex flex-col gap-4 ">
      <MeasureCardRow measures={measures} totalsData={totalsData} testid="profitability-measure-card-row" />
      <ProfitabilityWaterfallChart data={totalsData} />
      {feesAndCostsData && (
        <>
          {showCostOfGoods && (
            <CostOfGoodsList
              values={feesAndCostsData.values}
              compareValues={feesAndCostsData.compareValues}
              isComparisonActive={isComparisonActive}
            />
          )}
          <FeesList
            values={feesAndCostsData.values}
            compareValues={feesAndCostsData.compareValues}
            isComparisonActive={isComparisonActive}
          />
          <LogisticsCostsList
            values={feesAndCostsData.values}
            compareValues={feesAndCostsData.compareValues}
            isComparisonActive={isComparisonActive}
          />
        </>
      )}

      <ToggleButtonGroup
        className="mb-auto mt-auto"
        exclusive
        value={tableAttribute}
        onChange={(_event, value: AttributeCode | null) => {
          if (value !== null) setTableAttribute(value);
        }}
      >
        {availableAttributeCodes.map(dim => (
          <StyledToggleButton
            key={dim}
            value={dim}
            size="small"
            className="whitespace-nowrap text-xs"
            sx={{ color: '#1A1A1A' }}
          >
            {intl.formatMessage({ id: `profitability.drillDown.dimension.${dim}` })}
          </StyledToggleButton>
        ))}
      </ToggleButtonGroup>
      <DataTableContextProvider>
        <DataTable
          titleId={tableTitleId}
          rows={tableData ?? []}
          columns={columns}
          defaultSortingState={[{ id: 'netSlsGmvValExtExpOD', desc: true }]}
          onRowClick={getRowClick()}
          exportName={`profitability-lens-${tableAttribute}`}
          testId={`profitability-lens-${tableAttribute}-table`}
        />
      </DataTableContextProvider>
    </div>
  );
}
